import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import MuiIconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Moon, Sun } from 'lucide-react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/cn';
import type { IconButtonLook } from '@/theme/looks';
import { ICON_SIZE } from './iconSize';

const VARIANT = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'tonal', color: 'primary' },
  ghost: { variant: 'text', color: 'inherit' },
  danger: { variant: 'contained', color: 'error' },
} as const satisfies Record<string, Pick<MuiButtonProps, 'variant' | 'color'>>;

const SIZE = { sm: 'small', md: 'medium', lg: 'large' } as const;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  ref?: Ref<HTMLButtonElement>;
  variant?: keyof typeof VARIANT;
  size?: keyof typeof SIZE;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

/** Shows an MUI button in one of the kit variants. Forwards its ref to the button element. */
export function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    type = 'button',
    disabled,
    className,
    children,
    ref,
    ...props
  }: ButtonProps,
) {
  return (
    <MuiButton
      ref={ref}
      type={type}
      {...VARIANT[variant]}
      size={SIZE[size]}
      fullWidth={fullWidth}
      loading={loading}
      loadingPosition="start"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      startIcon={iconLeft}
      endIcon={loading ? undefined : iconRight}
      className={className}
      {...props}
    >
      {children}
    </MuiButton>
  );
}

const Root = styled(MuiIconButton, { name: 'KitIconButton', slot: 'root' })<{ ownerState: IconButtonLook }>({});

const ICON_BUTTON_SIZE = { sm: 'small', md: 'medium' } as const;

const ICON_BUTTON_VARIANT = {
  ghost: 'inherit',
  secondary: 'primary',
  danger: 'error',
  ghostDanger: 'inherit',
  overlayDanger: 'inherit',
} as const;

const DANGER: Record<keyof typeof ICON_BUTTON_VARIANT, IconButtonLook['danger']> = {
  ghost: 'none',
  secondary: 'none',
  danger: 'none',
  ghostDanger: 'quiet',
  overlayDanger: 'overlay',
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  ref?: Ref<HTMLButtonElement>;
  /** Accessible name for the icon-only button. */
  'aria-label': string;
  size?: keyof typeof ICON_BUTTON_SIZE;
  /** `ghostDanger` turns red on hover; `overlayDanger` also has a see-through fill to sit over an image. */
  variant?: keyof typeof ICON_BUTTON_VARIANT;
  loading?: boolean;
  /** Gives a low drop shadow, for a button that sits over other content. */
  raised?: boolean;
}

/** Shows an icon-only MUI button in one of the kit variants. */
export function IconButton({
  size = 'md',
  variant = 'ghost',
  loading = false,
  raised = false,
  type = 'button',
  disabled,
  className,
  children,
  ...props
}: IconButtonProps) {
  return (
    <Root
      type={type}
      color={ICON_BUTTON_VARIANT[variant]}
      size={ICON_BUTTON_SIZE[size]}
      loading={loading}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      ownerState={{ danger: DANGER[variant], raised }}
      className={className}
      {...props}
    >
      {children}
    </Root>
  );
}

/** The looks of a plain button or link: none, underlined on hover, or a tile that grows on hover. */
const PLAIN_LOOK = {
  plain: '',
  link: 'hover:underline',
  tile: 'rounded-lg transition-transform hover:scale-105',
} as const;

type PlainLook = keyof typeof PLAIN_LOOK;

export interface PlainButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>;
  /** `link` underlines on hover; `tile` has a corner and grows on hover. */
  variant?: PlainLook;
}

/** Shows a button with no fill and no border, around content that has its own look. */
export function PlainButton({ variant = 'plain', type = 'button', className, ref, ...props }: PlainButtonProps) {
  return <button ref={ref} type={type} className={cn(PLAIN_LOOK[variant], className)} {...props} />;
}

export interface TextLinkProps {
  /** An app route. */
  to: string;
  /** Uses the small bold type step. */
  small?: boolean;
  className?: string;
  children: ReactNode;
}

/** Shows an in-app link in the brand colour, underlined on hover. */
export function TextLink({ to, small = false, className, children }: TextLinkProps) {
  return (
    <Link component={RouterLink} to={to} variant={small ? 'label' : 'body1'} underline="hover" className={className}>
      {children}
    </Link>
  );
}

export interface ExternalLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> {
  ref?: Ref<HTMLAnchorElement>;
  href: string;
  /** `link` underlines on hover; `tile` has a corner and grows on hover. */
  variant?: PlainLook;
}

/** Shows a link that opens its address in a new tab, with no access to this page. */
export function ExternalLink({ variant = 'plain', className, ref, children, ...props }: ExternalLinkProps) {
  return (
    <a ref={ref} target="_blank" rel="noopener noreferrer" className={cn(PLAIN_LOOK[variant], className)} {...props}>
      {children}
    </a>
  );
}

export interface ThemeToggleProps {
  /** The translated name of the button. Without it the name is "Toggle theme". */
  label?: string;
  size?: 'sm' | 'md';
}

/** Flips the theme between light and dark. */
export function ThemeToggle({ label, size = 'md' }: ThemeToggleProps) {
  const { t } = useTranslation();
  const name = label ?? t('common.toggleTheme');
  const { isDark, toggle } = useTheme();

  return (
    <IconButton aria-label={name} title={name} size={size} onClick={toggle}>
      {isDark ? <Sun size={ICON_SIZE.lg} /> : <Moon size={ICON_SIZE.lg} />}
    </IconButton>
  );
}
